import { Capacitor } from '@capacitor/core';
import type { LocalNotificationActionPerformed } from '@capacitor/local-notifications';
import { LocalNotifications } from '@capacitor/local-notifications';
import type { ActionPerformed, Token } from '@capacitor/push-notifications';
import { PushNotifications } from '@capacitor/push-notifications';
import { FirebaseMessaging } from '@capacitor-firebase/messaging';
import { useAuthStore } from '../stores/auth.store';
import { callTapFromSearch, useIncomingCallStore } from '../stores/incoming-call.store';
import { useNotificationRouteStore } from '../stores/notification-route.store';
import { routeForPushTap } from '../stores/ring.store';
import { beginSessionFlow, onSessionChange, withSession } from '../stores/session-read';
import { fireAndForget } from '../utils/fireAndForget';
import { trackGaEvent } from '../utils/ga';
import { isLiveSessionPath } from '../utils/liveSessionPath';
import { isIncomingCallAction, resolveNotificationRoute } from './notification-router';
import {
  getPushStatus,
  reportNotificationOpened,
  storePushToken,
} from './push-notification.service';

let pushInitialized = false;
let tapListenersRegistered = false;
let foregroundListenerRegistered = false;
let _navigate: ((path: string) => unknown) | null = null;

/**
 * Call once from AppInit to provide the navigation function.
 * Accepts Promise- or void-returning navigators (react-router v7's navigate
 * returns Promise<void>; legacy callers may pass sync functions).
 */
export function setNotificationNavigator(fn: (path: string) => unknown): void {
  _navigate = fn;
}

/**
 * RING-PUSH: the button a notification was answered with, when it is one of
 * the incoming call's (Join / Not now). Android's call notification carries
 * it in its data (`call_action`, IncomingCallNotifier); a platform that
 * reports it as the action id (iOS categories, local notifications) is read
 * the same way. A plain tap is 'tap' and adds nothing.
 */
function withCallAction(data: Record<string, string>, actionId?: string): Record<string, string> {
  if (data.call_action || !isIncomingCallAction(actionId)) return data;
  return { ...data, call_action: actionId };
}

function handleDeepLink(data: Record<string, string>, actionId?: string): void {
  if (!data?.type) return;

  // Report tap to backend for open-rate analytics (fire-and-forget)
  void reportNotificationOpened(data.type);

  const payload = withCallAction(data, actionId);
  // The native payload is a plain string map; the router reads `type`, the
  // ids (numbers only) and an optional `route` (an /app/ path only), and an
  // unknown type lands by its prefix (notification-routes.ts), so an unsafe
  // shape can't navigate the user anywhere harmful.
  // P7d: a push that IS a ring (ring_id) and the in-app sheet are one ring:
  // the tap closes the sheet, or opens the live call if the sheet's Accept
  // already won. RING-PUSH: the same function decides for an incoming call.
  const route = routeForPushTap(payload, resolveNotificationRoute(payload));
  // #1009 item 4: a call tap is held until the user is signed in
  // (ListenerRingHost runs it once): the pending route above belongs to the
  // session it arrived under, so a tap made signed out would otherwise be
  // gone after the sign-in.
  const tap = callTapFromSearch(route.split('?')[1] ?? '');
  if (tap !== null) useIncomingCallStore.getState().holdPushTap(tap);
  useNotificationRouteStore.getState().setPendingRoute(route);
  _navigate?.(route);
}

/**
 * RING-PUSH: a call push that arrived while the app is open: the requester's
 * incoming call, or a listener's ring (ring_id; #1009 item 1, class D). The
 * in-app sheet is the ringer for both (the native service draws nothing in
 * the foreground either, IncomingCallNotifier.shouldDraw), so no local
 * notification is drawn; the poll runs now so the sheet shows within one
 * round trip. Inside a live session no sheet can show (ListenerRingHost is
 * off there), so the push falls back to the plain local notification.
 * Exported for the test.
 */
export function handleForegroundPush(data: Record<string, string> | undefined): boolean {
  const ringId = Number(data?.ring_id);
  const isCall = data?.type === 'empathy_request_claimed';
  const isRing = Number.isInteger(ringId) && ringId > 0;
  if (!isCall && !isRing) return false;
  if (isLiveSessionPath(window.location.pathname)) return false;
  // Imported on use: this module loads first thing at boot (cold-start
  // taps), and the feed store pulls in the call stack behind it.
  void import('../stores/active-cohort-sessions.store').then(({ useActiveCohortSessionsStore }) =>
    useActiveCohortSessionsStore.getState().pollNow(),
  );
  return true;
}

/**
 * The local notification a foreground push is shown as (when the sheet is not
 * the ringer). A data-only call (the build draws it itself) carries its title
 * and body in the data. Exported for the test.
 */
export function foregroundLocalNotification(notification: {
  title?: string;
  body?: string;
  data?: unknown;
}): { title: string; body: string; extra: Record<string, string> } {
  const data = (notification.data ?? {}) as Record<string, string>;
  return {
    title: notification.title ?? data.title ?? 'Notification',
    body: notification.body ?? data.body ?? '',
    extra: data,
  };
}

/**
 * Register notification tap listeners as early as possible.
 * This MUST run before auth so that cold-start notification
 * taps are captured (the native event fires before JS is fully ready).
 * Safe to call multiple times — only registers once.
 */
export function registerNotificationTapListeners(): void {
  if (tapListenersRegistered) return;
  if (!Capacitor.isNativePlatform()) return;

  // Background / killed — user tapped FCM notification
  void PushNotifications.addListener(
    'pushNotificationActionPerformed',
    (action: ActionPerformed) => {
      handleDeepLink(action.notification.data ?? {}, action.actionId);
    },
  );

  // Foreground — user tapped the local notification.
  // The Capacitor local-notifications type declares `extra` as `any`; narrow
  // it to the deep-link payload shape we actually use.
  void LocalNotifications.addListener(
    'localNotificationActionPerformed',
    (action: LocalNotificationActionPerformed) => {
      const extra = action.notification.extra as Record<string, string> | null | undefined;
      handleDeepLink(extra ?? {}, action.actionId);
    },
  );

  tapListenersRegistered = true;

  // Safety net: if no notification event arrives within 500ms, resolve anyway
  setTimeout(() => {
    useNotificationRouteStore.getState().markResolved();
  }, 500);
}

/**
 * Initializes push notifications by requesting permission, registering the device,
 * storing the FCM token on the backend, and updating the auth store with push status.
 * Runs only once per session and returns the FCM token or null on failure.
 */
/**
 * THE one way to ask for push permission (Codex rounds 10-11 on #816):
 * every ask site must route through here so push_permission counts
 * DECISIONS exactly once. checkPermissions() beforehand gates the event on
 * an actual ask — requestPermissions() merely echoes the status for
 * already-decided users, and two sites asking independently either double
 * counted (round 1) or hid Profile's grants and denials entirely
 * (round 11, after the round-10 decision gate).
 */
export async function requestPushPermissionTracked(): Promise<{ receive: string }> {
  const before = await PushNotifications.checkPermissions();
  const permission = await PushNotifications.requestPermissions();
  if (before.receive === 'prompt' || before.receive === 'prompt-with-rationale') {
    trackGaEvent('push_permission', { granted: permission.receive === 'granted' });
  }
  return permission;
}

/**
 * GET /push-token, bound to the session it was sent under (session-read.ts):
 * resolves `null` when that session ended before the answer arrived, so a
 * caller that writes the status onto the signed-in user cannot put account A's
 * status on account B (#940). Every reader that writes the status uses this.
 */
export async function getPushStatusForSession(): Promise<Awaited<
  ReturnType<typeof getPushStatus>
> | null> {
  const outcome = await withSession(() => getPushStatus());
  if (outcome.status === 'ended') return null;
  if (outcome.status === 'failed') throw outcome.error;
  return outcome.value;
}

export async function initPushNotifications(): Promise<string | null> {
  if (pushInitialized) return null;
  if (!Capacitor.isNativePlatform()) return null;

  // Set immediately to prevent concurrent callers from adding duplicate listeners
  pushInitialized = true;
  // Captured before the first await (#940 r7): registration waits on the OS
  // and two round trips; if the session changes meanwhile, the device token
  // and status are not recorded for whoever is signed in by then (that
  // session's own init does it; sign-out resets pushInitialized).
  const flow = beginSessionFlow();

  // Ensure tap listeners are registered (no-op if already done)
  registerNotificationTapListeners();

  const permission = await requestPushPermissionTracked();
  if (permission.receive !== 'granted') return null;

  // Foreground — show as local notification, carry data for deep link.
  // Guarded to ensure the listener is only ever registered once.

  if (!foregroundListenerRegistered) {
    // Both iOS and Android: FirebaseMessaging fires 'notificationReceived' for foreground messages
    void FirebaseMessaging.addListener(
      'notificationReceived',
      fireAndForget(async (event) => {
        if (handleForegroundPush(event.notification.data as Record<string, string> | undefined)) {
          return;
        }
        if (Capacitor.getPlatform() === 'ios') return;
        const { title, body, extra } = foregroundLocalNotification(event.notification);
        try {
          await LocalNotifications.schedule({
            notifications: [
              {
                id: Date.now() % 2147483647,
                title,
                body,
                schedule: { at: new Date(Date.now() + 100) },
                smallIcon: 'ic_stat_notification',
                extra,
              },
            ],
          });
        } catch (err) {
          console.error(err);
        }
      }),
    );
    if (Capacitor.getPlatform() === 'ios') {
      void FirebaseMessaging.addListener('notificationActionPerformed', (event) => {
        handleDeepLink((event.notification.data as Record<string, string>) ?? {}, event.actionId);
      });
    }
    foregroundListenerRegistered = true;
  }

  await PushNotifications.register();

  let token: string | null = null;
  if (Capacitor.getPlatform() === 'ios') {
    const result = await FirebaseMessaging.getToken();
    token = result.token;
  } else {
    token = await new Promise<string | null>((resolve) => {
      void PushNotifications.addListener('registration', (t: Token) => resolve(t.value));
      void PushNotifications.addListener('registrationError', () => resolve(null));
    });
  }

  if (token) {
    const stored = await flow.step(() => storePushToken(token as string));
    if (stored.status !== 'ok') return token;
    const result = stored.value;
    if (result.success) {
      try {
        // Registration waits on the OS and two round trips: the session can
        // change underneath it (#940), so read the status bound to its session.
        const read = await flow.step(() => getPushStatusForSession());
        const pushResponse = read.status === 'ok' ? read.value : null;
        if (!pushResponse?.success) return token;
        const { user } = useAuthStore.getState();
        if (!user) return token;
        useAuthStore.setState({
          user: {
            ...user,
            push_enabled: pushResponse.data.push_enabled,
            push_platform: pushResponse.data.push_platform,
          },
        });
      } catch (error) {
        console.error(error);
      }
    }
  }

  return token;
}

export function resetPushInitialized(): void {
  pushInitialized = false;
}

// Push registration belongs to the session that ran it: any session change
// (not only an explicit sign-out) lets the next session register the device
// for itself, and an init that ended with its session leaves nothing marked
// done (#940 r8).
onSessionChange(() => {
  pushInitialized = false;
});

// Register tap listeners at module load time so cold-start and
// background-resume notification taps are captured immediately.
registerNotificationTapListeners();
